import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/layouts/PageHeader";
import {
  LucideTrash,
  LucidePlus,
  LucideBookOpen,
  LucidePencil,
  LucideCopy,
} from "lucide-react";
import ReportFolderList from "@/components/entities/ReportFolderList";
import { EmptyState } from "@/components/ui/EmptyState";
import { useSchool } from "@/context/SchoolContext";
import { CreateReportFolderModal } from "@/components/modals/CreateReportFolderModal";
import { EditReportFolderModal } from "@/components/modals/EditReportFolderModal";
import { DuplicateReportFolderModal } from "@/components/modals/DuplicateReportFolderModal";
import { LimitExceededModal } from "@/components/modals/LimitExceededModal";

export default function ReportFolders() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { memberships } = useSchool();

  const [reportFolders, setReportFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingReportFolder, setEditingReportFolder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [duplicatingReportFolder, setDuplicatingReportFolder] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [limitMessage, setLimitMessage] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetchReportFolders = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/report-folders`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch Report Folders");
        setReportFolders(await res.json());
        setFetchError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching Report Folders:", err);
        setFetchError(
          "Could not load your Report Folders. Please refresh the page."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchReportFolders();
    return () => controller.abort();
  }, [getToken]);

  const handleCreate = async (formData) => {
    setCreating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-folders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.limitExceeded) {
          setShowModal(false);
          setLimitMessage(body.message);
          return;
        }
        throw new Error("Failed to create Report Folder");
      }
      setReportFolders((prev) => [...prev, body]);
      setShowModal(false);
      navigate(`/report-folders/${body._id}`);
    } catch (err) {
      console.error("Error creating Report Folder:", err);
      toast.error("Failed to create Report Folder. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (reportFolder) => {
    const reportFolderLabel =
      reportFolder.name ||
      [reportFolder.grade && `Grade ${reportFolder.grade}`, reportFolder.schoolYear]
        .filter(Boolean)
        .join(" · ") ||
      "this Report Folder";
    if (
      !confirm(
        `Delete "${reportFolderLabel}"? This will also delete all report cards in this Report Folder.`
      )
    )
      return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/report-folders/${reportFolder._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportFolders((prev) => prev.filter((c) => c._id !== reportFolder._id));
      toast.success("Report Folder deleted.");
    } catch (err) {
      console.error("Error deleting Report Folder:", err);
      toast.error("Failed to delete Report Folder. Please try again.");
    }
  };

  const handleUpdate = async (reportFolder, editForm) => {
    setUpdating(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/report-folders/${reportFolder._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );
      if (!res.ok) throw new Error("Failed to update Report Folder");
      const updated = await res.json();
      setReportFolders((prev) =>
        prev.map((c) => (c._id === updated._id ? updated : c))
      );
      setEditingReportFolder(null);
      toast.success("Report Folder updated.");
    } catch (err) {
      console.error("Error updating Report Folder:", err);
      toast.error("Failed to update Report Folder. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleDuplicate = async (reportFolder, name) => {
    setDuplicating(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${BACKEND_URL}api/report-folders/${reportFolder._id}/duplicate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.limitExceeded) {
          setDuplicatingReportFolder(null);
          setLimitMessage(body.message);
          return;
        }
        throw Object.assign(
          new Error(body.message || "Failed to duplicate Report Folder"),
          { status: res.status }
        );
      }
      setReportFolders((prev) => [body, ...prev]);
      setDuplicatingReportFolder(null);
      toast.success("Report Folder duplicated successfully.");
    } catch (err) {
      console.error("Error duplicating Report Folder:", err);
      if (err.status === 403) {
        toast.error(err.message, { duration: 6000 });
      } else {
        toast.error("Failed to duplicate Report Folder. Please try again.");
      }
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <>
      <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
        <PageHeader
          title="My Report Folders"
          subtitle="Each Report Folder holds report cards for one reporting cycle. Create a new Report Folder, then add a report inside it for each student."
          action={
            !loading && reportFolders.length > 0 ? (
              <Button onClick={() => setShowModal(true)} className="gap-2">
                <LucidePlus size={15} />
                New Report Folder
              </Button>
            ) : null
          }
        />

        {fetchError && (
          <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {fetchError}
          </div>
        )}

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : reportFolders.length === 0 ? (
          <EmptyState
            icon={LucideBookOpen}
            title="No Report Folders yet"
            description="Create your first Report Folder to start generating report cards."
            action={
              <Button
                onClick={() => setShowModal(true)}
                variant="outline"
                className="gap-2"
              >
                <LucidePlus size={14} />
                New Report Folder
              </Button>
            }
          />
        ) : (
        <ReportFolderList
          items={reportFolders}
          getLinkPath={(item) => `/report-folders/${item._id}`}
          renderNameExtra={(item) =>
            item.reportsCount !== undefined ? (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full shrink-0">
                {item.reportsCount}{" "}
                {item.reportsCount === 1 ? "report" : "reports"}
              </span>
            ) : null
          }
          getMenuItems={(item) => [
            { label: "Edit", icon: LucidePencil, onClick: () => setEditingReportFolder(item) },
            { label: "Duplicate", icon: LucideCopy, onClick: () => setDuplicatingReportFolder(item) },
            { label: "Delete", icon: LucideTrash, onClick: () => handleDelete(item), destructive: true },
          ]}
        />
        )}
      </div>

      <CreateReportFolderModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleCreate}
        creating={creating}
      />

      <EditReportFolderModal
        reportFolder={editingReportFolder}
        onClose={() => setEditingReportFolder(null)}
        onSubmit={handleUpdate}
        updating={updating}
      />

      <DuplicateReportFolderModal
        reportFolder={duplicatingReportFolder}
        onClose={() => setDuplicatingReportFolder(null)}
        onSubmit={handleDuplicate}
        duplicating={duplicating}
      />

      <LimitExceededModal
        open={!!limitMessage}
        message={limitMessage}
        onClose={() => setLimitMessage(null)}
      />
    </>
  );
}
